import { DEFAULT_CONFIG } from '@shared/constants';

export class ImageUploadManager {
  private element: HTMLElement;

  constructor(panelElement: HTMLElement) {
    this.element = panelElement;
  }

  setupDragAndDrop(): void {
    const uploadArea = this.element.querySelector('[data-ce="upload-area"]');
    if (!uploadArea) return;

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
      uploadArea.addEventListener(eventName, this.preventDefaults, false);
      document.body.addEventListener(eventName, this.preventDefaults, false);
    });

    ['dragenter', 'dragover'].forEach((eventName) => {
      uploadArea.addEventListener(eventName, this.highlight.bind(this), false);
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      uploadArea.addEventListener(eventName, this.unhighlight.bind(this), false);
    });

    uploadArea.addEventListener(
      'drop',
      (e: Event) => {
        const dragEvent = e as DragEvent;
        this.handleDrop(dragEvent);
      },
      false,
    );
  }

  private preventDefaults(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
  }

  private highlight(): void {
    const uploadArea = this.element.querySelector('[data-ce="upload-area"]') as HTMLElement;
    if (uploadArea) {
      uploadArea.classList.add('dragover');
    }
  }

  private unhighlight(): void {
    const uploadArea = this.element.querySelector('[data-ce="upload-area"]') as HTMLElement;
    if (uploadArea) {
      uploadArea.classList.remove('dragover');
    }
  }

  private handleDrop(e: DragEvent): void {
    const dt = e.dataTransfer;
    if (!dt) return;
    const files = dt.files;

    if (files.length > 0) {
      const file = files[0];
      this.handleImageFileUpload(file);
    }
  }

  handleImageUploadClick(): void {
    const fileInput = document.getElementById('ce-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  handleRemoveImage(): void {
    this.clearImageUploadToken();
    this.updateImagePreview(null, null);
    this.showImageUploadStatus(false);
  }

  private updateImagePreview(imageUrl: string | null, fileName: string | null): void {
    const preview = this.element.querySelector('[data-ce="image-preview"]') as HTMLElement;
    const previewImg = this.element.querySelector('[data-ce="preview-img"]') as HTMLImageElement;
    const imageName = this.element.querySelector('[data-ce="image-name"]') as HTMLElement;
    const uploadArea = this.element.querySelector('[data-ce="upload-area"]') as HTMLElement;

    if (imageUrl && fileName && previewImg && imageName && uploadArea) {
      previewImg.src = imageUrl;
      imageName.textContent = fileName;
      preview.style.display = '';
      uploadArea.style.display = 'none';
    } else if (preview && uploadArea) {
      preview.style.display = 'none';
      uploadArea.style.display = '';
    }
  }

  private showImageUploadStatus(show: boolean): void {
    const status = this.element.querySelector('[data-ce="upload-status"]') as HTMLElement;
    const uploadArea = this.element.querySelector('[data-ce="upload-area"]') as HTMLElement;
    const preview = this.element.querySelector('[data-ce="image-preview"]') as HTMLElement;

    if (show && status && uploadArea && preview) {
      status.style.display = '';
      uploadArea.style.display = 'none';
      preview.style.display = 'none';
    } else if (status && uploadArea && preview) {
      status.style.display = 'none';
      if (this.getImageUploadToken()) {
        preview.style.display = '';
      } else {
        uploadArea.style.display = '';
      }
    }
  }

  async handleImageFileUpload(
    file: File,
    sessionToken?: string,
    pageUrl?: string,
  ): Promise<{ token: string; url: string }> {
    const validation = this.validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    this.showImageUploadStatus(true);

    try {
      const formData = new FormData();
      formData.append('image', file);
      if (sessionToken) {
        formData.append('token', sessionToken);
      }
      if (pageUrl) {
        formData.append('page_url', pageUrl);
      }

      const platformOrigin = DEFAULT_CONFIG.platformOrigin;
      const response = await fetch(`${platformOrigin}/api/sdk/editor/upload-image`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.ok && result.token && result.url) {
        this.setImageUploadToken(result.token);
        this.updateImagePreview(result.url, file.name);
        this.showImageUploadStatus(false);
        return { token: result.token, url: result.url };
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      this.showImageUploadStatus(false);
      throw error;
    }
  }

  private validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Only JPEG, JPG, and PNG images are supported',
      };
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Image size must be less than 10MB',
      };
    }

    return { valid: true };
  }

  getImageUploadToken(): string | undefined {
    return this.element.dataset.ceImageToken || undefined;
  }

  getCurrentImageUrl(): string | undefined {
    const previewImg = this.element.querySelector('[data-ce="preview-img"]') as HTMLImageElement;
    return previewImg?.src || undefined;
  }

  setImageUploadToken(token: string): void {
    this.element.dataset.ceImageToken = token;
  }

  private clearImageUploadToken(): void {
    delete this.element.dataset.ceImageToken;
  }
}
